import { useRef } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Button from 'components/Button/Button';
import Modal from 'components/Modal/Modal';
import Select from 'components/Select/Select';
import TextField from 'components/TextField/TextField';
import {
  CONTACT_EMAIL,
  DEPARTMENT,
  EMPLOYEE_REPRESENTATIVE_NAME,
  ROLE,
} from 'constants/formFields';
import { OPTION_ROLE_EMPLOYEES } from 'constants/general';
import { BUTTON_VARIANT } from 'constants/interfaces';
import useFieldsErrors from 'hooks/useFieldsErrors';
import i18n from 'i18n';
import { useCreateMaterialitySHEmployeesMutation, useUpdateMaterialitySHEmployeesMutation } from 'store/slices/materialAssessment/apis/materialAssessmentApi';

import useCreateEmployeesForm from './hooks/useCreateEmployeesForm';

import type IMaterialSHEmployeeItem from 'store/slices/materialAssessment/interfaces/IMaterialSHEmployeeItem';
import type IFieldError from 'store/types/IFieldError';
import type { TEmployeesCreateSchema } from 'utils/validators';
import type ICreateEmployees from './interfaces/ICreateEmployees';

import styles from './scss/CreateEmployees.module.scss';

function CreateEmployees({
  open, onClose, title, selectedItem, recordId, currentId, setEmployees, setOpenModal,
  vcmId, employeesEnumList,
}: ICreateEmployees) {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);
  const [createEmployees] = useCreateMaterialitySHEmployeesMutation();
  const [updateEmployees] = useUpdateMaterialitySHEmployeesMutation();
  const { setFieldsErrors } = useFieldsErrors();
  const isLoading = false;

  const {
    control,
    handleSubmit,
    reset,
    setError,
    fieldsName,
  } = useCreateEmployeesForm({ isPageLoading: isLoading, selectedItem, employeesEnumList });

  const onCancel = () => {
    onClose();
    reset();
  };

  const onAddEmployees = (employee: IMaterialSHEmployeeItem) => {
    if (currentId !== -1) {
      updateEmployees({
        id: currentId, maId: recordId, data: employee, vcmId,
      }).then((request) => {
        if ('data' in request && request.data?.data) {
          toast.success(i18n.t('notifications.updatedEmployee'));
          setEmployees((prev) => {
            const index = prev.findIndex((item) => item.id === currentId);
            if (index !== -1) {
              const updatedEmployees = [...prev];
              updatedEmployees[index] = { ...updatedEmployees[index], ...request.data.data };
              return updatedEmployees;
            }
            return prev;
          });
          setOpenModal(false);
        }
      }).catch((errors) => {
        setFieldsErrors<TEmployeesCreateSchema>({ errors, setError, fieldsName });
      });
    } else {
      createEmployees({
        data: employee,
        maId: recordId,
        vcmId,
      }).then(() => {
        toast.success(i18n.t('notifications.createdEmployee'));
        setOpenModal(false);
      }).catch((errors) => {
        setFieldsErrors<TEmployeesCreateSchema>({ errors, setError, fieldsName });
      });
    }
  };

  const onCreateInvestorSubmit = handleSubmit((data) => {
    onAddEmployees({
      eployeeDepartment: data[DEPARTMENT]?.value || '',
      eployeeRole: data[ROLE]?.value || '',
      contactName: data[EMPLOYEE_REPRESENTATIVE_NAME] || '',
      contactEmail: data[CONTACT_EMAIL] || '',
    });
    onCancel();
  });

  return (
    <Modal
      open={open}
      title={title || ''}
      className={styles.modalContainer}
      isButtonsVisible={false}
    >
      <div className={styles.root} ref={modalRef}>
        <div className={styles.formWrapper}>
          <Controller
            control={control}
            name={DEPARTMENT}
            render={({
              field: {
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <Select
                label={t('materialityAssessment.fields.department')}
                placeholder={t('materialityAssessment.placeholder.selectDepartment')}
                options={employeesEnumList}
                value={value?.value ? value : undefined}
                onChange={onChange}
                error={(error as IFieldError)?.value?.message}
                modalRef={modalRef}
                menuPosition="absolute"
                required
              />
            )}
          />

          <Controller
            control={control}
            name={ROLE}
            render={({
              field: {
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <Select
                label={t('materialityAssessment.fields.role')}
                placeholder={t('materialityAssessment.placeholder.selectRole')}
                options={OPTION_ROLE_EMPLOYEES}
                value={value?.value ? value : undefined}
                onChange={onChange}
                error={(error as IFieldError)?.value?.message}
                modalRef={modalRef}
                menuPosition="absolute"
                required
              />
            )}
          />
          <Controller
            control={control}
            name={EMPLOYEE_REPRESENTATIVE_NAME}
            render={({
              field: {
                name,
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <TextField
                name={name}
                value={value}
                label={t('materialityAssessment.fields.employeeRepresentativeName')}
                error={error?.message}
                onChange={(event) => onChange(event.target.value)}
                required
                placeholder={t('materialityAssessment.placeholder.typeHere')}
              />
            )}
          />
          <Controller
            control={control}
            name={CONTACT_EMAIL}
            render={({
              field: {
                name,
                value,
                onChange,
              },
              fieldState: { error },
            }) => (
              <TextField
                name={name}
                value={value}
                label={t('materialityAssessment.fields.contactEmail')}
                error={error?.message}
                onChange={(event) => onChange(event.target.value)}
                placeholder={t('materialityAssessment.placeholder.typeHere')}
                required
              />
            )}
          />
        </div>
        <div className={styles.buttonWrapper}>
          <Button
            title={t('common.cancel')}
            variant={BUTTON_VARIANT.cancel}
            className={styles.button}
            onClick={onCancel}
          />
          <Button
            title={t('common.add')}
            onClick={onCreateInvestorSubmit}
            className={styles.button}
          />
        </div>
      </div>
    </Modal>
  );
}

export default CreateEmployees;
